/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
// Types for the cache entries
interface CacheErrorEntry {
	location: string;
	message: string;
	error?: Error;
}

interface CacheMetricEntry {
	spanType: 'fetch' | 'graphql' | 'relay' | 'custom';
	name: string;
	tags: Array<{ name: string }> | null;
	start: number;
	end?: number;
	size?: number;
}

export class EarlyScriptObservabilityCache {
	private errors: CacheErrorEntry[] = [];

	private metrics: CacheMetricEntry[] = [];

	private maxEntries = 1000; // Prevent unlimited growth

	// Error methods following the same pattern as flags.tsx
	recordError(location: string, message: string, error?: Error): void {
		const entry: CacheErrorEntry = {
			location,
			message,
			error,
		};

		this.errors.push(entry);
		this.trimErrors();
	}

	// Metric methods following the same pattern as addSpanToAll
	recordMetric(
		spanType: 'fetch' | 'graphql' | 'relay' | 'custom',
		name: string,
		tags: Array<{ name: string }> | null,
		start: number,
		end: number,
		size?: number,
	): void {
		const entry: CacheMetricEntry = {
			spanType,
			name,
			tags,
			start,
			end,
			size,
		};

		this.metrics.push(entry);
		this.trimMetrics();
	}

	// Retrieval methods
	getErrors(): CacheErrorEntry[] {
		return [...this.errors];
	}

	getMetrics(): CacheMetricEntry[] {
		return [...this.metrics];
	}

	// Clear methods
	clearErrors(): void {
		this.errors = [];
	}

	clearMetrics(): void {
		this.metrics = [];
	}

	clearAll(): void {
		this.clearErrors();
		this.clearMetrics();
	}

	// Utility methods for cache maintenance
	private trimErrors(): void {
		if (this.errors.length > this.maxEntries) {
			this.errors = this.errors.slice(-this.maxEntries);
		}
	}

	private trimMetrics(): void {
		if (this.metrics.length > this.maxEntries) {
			this.metrics = this.metrics.slice(-this.maxEntries);
		}
	}
}

// Export singleton instance
let earlyScriptObservabilityCache: EarlyScriptObservabilityCache | null = null;

export const getEarlyScriptObservabilityCache = (): EarlyScriptObservabilityCache => {
	if (!earlyScriptObservabilityCache) {
		earlyScriptObservabilityCache = new EarlyScriptObservabilityCache();
	}
	return earlyScriptObservabilityCache;
};

// Export types for consumers
export type { CacheErrorEntry, CacheMetricEntry };
